import React from "react";
import { Button, Typography } from "@firecms/ui";
import type { BatchItemResult } from "./types";

interface BatchResultBarProps {
  label: string;
  results: BatchItemResult[] | null;
  onRetryFailed?: () => void;
  retryDisabled?: boolean;
}

/**
 * Shows last batch outcome: success/fail counts and expandable failed IDs.
 */
export function BatchResultBar({
  label,
  results,
  onRetryFailed,
  retryDisabled,
}: BatchResultBarProps) {
  if (!results?.length) return null;
  const failed = results.filter((r) => !r.ok);
  const ok = results.filter((r) => r.ok).length;
  return (
    <div
      className="mt-3 p-3 rounded border border-gray-200 bg-gray-50"
      role="status"
      aria-live="polite"
    >
      <Typography variant="body2" className="font-medium">
        {label}: {ok} succeeded, {failed.length} failed (of {results.length})
      </Typography>
      {failed.length > 0 && (
        <>
          <ul className="mt-2 text-sm text-red-800 list-disc list-inside max-h-32 overflow-y-auto">
            {failed.map((f) => (
              <li key={f.id}>
                <span className="font-mono">{f.id}</span>
                {f.error ? ` — ${f.error}` : ""}
              </li>
            ))}
          </ul>
          {onRetryFailed && (
            <Button
              variant="outlined"
              size="small"
              className="mt-2"
              onClick={onRetryFailed}
              disabled={retryDisabled}
            >
              Retry failed only
            </Button>
          )}
        </>
      )}
    </div>
  );
}
